// src/components/profile/ForgotPasswordModal.js - Password reset modal
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../config/firebase';
import '../../styles/globals.css';
import './ForgotPasswordModal.css';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /**
   * Handle password reset email submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setError(null);
    
    // Validate email
    if (!email || !email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('Sending password reset email to:', email);
      // Send password reset email
      await sendPasswordResetEmail(auth, email);
      // If we create our own change password page: 
    //   await sendPasswordResetEmail(auth, email, {
        // Optional: Customize the redirect URL
        // url: 'https://your-app.com/login',
        // handleCodeInApp: false
    //   });
      
      console.log('Password reset email sent successfully');
      setSuccess(true);
      
      // Clear email field
      setEmail('');
      
    } catch (err) {
      console.error('Error sending password reset email:', err);
      
      // Handle specific Firebase errors
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      switch (err.code) {
        case 'auth/user-not-found':
          // For security reasons, we don't want to reveal if an email exists or not
          // So we'll show a success message anyway
          console.log('User not found, but showing success message for security');
          setSuccess(true);
          setEmail('');
          return;
          
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address. Please check and try again.';
          break;
          
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please wait a few minutes before trying again.';
          break;
          
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection and try again.';
          break;
          
        default:
          errorMessage = err.message || 'Failed to send password reset email. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setEmail('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content forgot-password-modal">
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">Reset Your Password</h3>
          <button
            onClick={handleClose}
            className="modal-close"
            aria-label="Close"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="forgot-password-success">
            <div className="success-icon">
              <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="success-title">Check Your Email</h4>
            <p className="success-message">
              If an account exists with the email you entered, you&apos;ll receive a password reset link shortly.
            </p>
            <p className="success-submessage">
              The link will expire in 1 hour for security reasons.
            </p>
            <div className="success-actions">
              <button
                onClick={handleClose}
                className="btn btn-primary w-full"
              >
                Got It
              </button>
            </div>
            <div className="success-help">
              <p className="text-sm text-gray-600">
                Didn&apos;t receive the email?
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="link-button text-sm"
              >
                Try another email address
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Instructions */}
            <div className="mb-4">
              <p className="text-sm text-gray-700">
                Enter your email address and we&apos;ll send you a link to reset your password.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="alert alert-error mb-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
                <button 
                  onClick={() => setError(null)}
                  className="alert-close"
                >
                  ×
                </button>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="form-group mb-6">
                <label htmlFor="reset-email" className="form-label">
                  Email Address <span className="text-error">*</span>
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="your.email@company.com"
                  disabled={loading}
                  autoFocus
                />
              </div>

              {/* Buttons */}
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn btn-secondary flex-1"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="loading-dots mr-2">
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                      </div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Send Reset Link
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Additional Help */}
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-600">
                <strong>Note:</strong> For security reasons, we&apos;ll send a reset link even if the email isn&apos;t registered. 
                If you don&apos;t receive an email within a few minutes, please check your spam folder.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ForgotPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ForgotPasswordModal;